"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.clampBasisPoints = exports.isValidBasisPoints = exports.formatBasisPointsShort = exports.formatBasisPoints = exports.calculateAmount = exports.toPercentage = exports.fromPercentage = exports.BasisPointsService = void 0;
const primitives_1 = require("../../primitives");
/**
 * Centralized Basis Points Service for zlinkx
 *
 * This service provides consistent percentage handling using basis points.
 * Basis points avoid floating-point precision issues by representing percentages as integers.
 *
 * 1 basis point = 0.01%
 * 100 basis points = 1%
 * 10,000 basis points = 100%
 *
 * Example: 2.5% = 250 basis points, 60.5% = 6050 basis points
 */
class BasisPointsService {
    /**
     * Maximum valid basis points (100%)
     */
    static MAX_BASIS_POINTS = primitives_1.MAX_BASIS_POINTS;
    /**
     * Conversion factor from percentage to basis points
     */
    static PERCENTAGE_TO_BP_FACTOR = primitives_1.PERCENTAGE_TO_BASIS_POINTS_FACTOR;
    /**
     * Convert percentage to basis points
     * @param percentage - Percentage value (e.g., 60.5 for 60.5%)
     * @returns Basis points as integer (e.g., 6050)
     */
    static fromPercentage(percentage) {
        if (typeof percentage !== 'number' || isNaN(percentage)) {
            return 0;
        }
        return Math.round(percentage * this.PERCENTAGE_TO_BP_FACTOR);
    }
    /**
     * Convert basis points to percentage
     * @param basisPoints - Basis points (e.g., 6050)
     * @returns Percentage value (e.g., 60.5)
     */
    static toPercentage(basisPoints) {
        if (typeof basisPoints !== 'number' || isNaN(basisPoints)) {
            return 0;
        }
        return basisPoints / this.PERCENTAGE_TO_BP_FACTOR;
    }
    /**
     * Calculate amount based on basis points
     * Use this for commission calculations, discounts, etc.
     * @param amountCents - Base amount in cents
     * @param basisPoints - Percentage in basis points (e.g., 250 for 2.5%)
     * @returns Calculated amount in cents (rounded to integer)
     */
    static calculateAmount(amountCents, basisPoints) {
        if (typeof amountCents !== 'number' || typeof basisPoints !== 'number') {
            return 0;
        }
        if (isNaN(amountCents) || isNaN(basisPoints)) {
            return 0;
        }
        return Math.round((amountCents * basisPoints) / this.MAX_BASIS_POINTS);
    }
    /**
     * Format basis points as percentage string with full precision
     * @param basisPoints - Basis points (e.g., 6050)
     * @returns Formatted string (e.g., "60.50%")
     */
    static format(basisPoints) {
        if (typeof basisPoints !== 'number' || isNaN(basisPoints)) {
            return '0.00%';
        }
        const percentage = this.toPercentage(basisPoints);
        return `${percentage.toFixed(2)}%`;
    }
    /**
     * Format basis points as percentage string (short format)
     * Removes trailing zeros for cleaner display
     * @param basisPoints - Basis points (e.g., 6050)
     * @returns Formatted string (e.g., "60.5%" or "60%")
     */
    static formatShort(basisPoints) {
        if (typeof basisPoints !== 'number' || isNaN(basisPoints)) {
            return '0%';
        }
        const percentage = this.toPercentage(basisPoints);
        // Remove trailing zeros
        if (percentage % 1 === 0) {
            return `${percentage.toFixed(0)}%`;
        }
        // One decimal place if needed
        const oneDecimal = Math.round(percentage * 10) / 10;
        if (oneDecimal === percentage) {
            return `${percentage.toFixed(1)}%`;
        }
        return `${percentage.toFixed(2)}%`;
    }
    /**
     * Validate if a value is valid basis points
     * @param basisPoints - Value to validate
     * @returns True if valid (integer between 0 and 10000)
     */
    static isValid(basisPoints) {
        return (typeof basisPoints === 'number' &&
            Number.isInteger(basisPoints) &&
            basisPoints >= 0 &&
            basisPoints <= this.MAX_BASIS_POINTS);
    }
    /**
     * Clamp basis points to valid range
     * @param basisPoints - Value to clamp
     * @returns Clamped value between 0 and 10000
     */
    static clamp(basisPoints) {
        if (typeof basisPoints !== 'number' || isNaN(basisPoints)) {
            return 0;
        }
        return Math.max(0, Math.min(this.MAX_BASIS_POINTS, Math.round(basisPoints)));
    }
    /**
     * Add two basis point values safely
     * @param a - First basis points value
     * @param b - Second basis points value
     * @returns Sum, clamped to valid range
     */
    static add(a, b) {
        return this.clamp(a + b);
    }
    /**
     * Subtract basis points safely
     * @param a - Value to subtract from
     * @param b - Value to subtract
     * @returns Difference, clamped to valid range
     */
    static subtract(a, b) {
        return this.clamp(a - b);
    }
}
exports.BasisPointsService = BasisPointsService;
// Export utility functions for direct use
exports.fromPercentage = BasisPointsService.fromPercentage.bind(BasisPointsService);
exports.toPercentage = BasisPointsService.toPercentage.bind(BasisPointsService);
exports.calculateAmount = BasisPointsService.calculateAmount.bind(BasisPointsService);
exports.formatBasisPoints = BasisPointsService.format.bind(BasisPointsService);
exports.formatBasisPointsShort = BasisPointsService.formatShort.bind(BasisPointsService);
exports.isValidBasisPoints = BasisPointsService.isValid.bind(BasisPointsService);
exports.clampBasisPoints = BasisPointsService.clamp.bind(BasisPointsService);
